import React, { useEffect, useState, useRef } from 'react';
import ReactPlayer from 'react-player';

interface MediaItem {
  url: string;
  type: 'image' | 'video' | 'youtube';
}

interface CarouselMediaProps {
  mediaList: MediaItem[];
  interval: number; // en milisegundos
  enableAudio?: boolean;
  useNativeVideo?: boolean; // Para usar elemento video nativo en lugar de ReactPlayer
}

const CarouselMedia: React.FC<CarouselMediaProps> = ({ mediaList, interval, enableAudio = false, useNativeVideo = false }) => {
  const [current, setCurrent] = useState(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [videoDuration, setVideoDuration] = useState<number>(0);
  const [userInteracted, setUserInteracted] = useState<boolean>(false);

  // Función para procesar URLs de YouTube
  const processYouTubeUrl = (url: string): string => {
    // Si ya es una URL válida de YouTube, la devolvemos tal como está
    if (url.includes('youtube.com/watch')) {
      return url;
    }
    
    // Si es solo el ID del video, construimos la URL completa
    if (url.length === 11 && /^[a-zA-Z0-9_-]{11}$/.test(url)) {
      return `https://www.youtube.com/watch?v=${url}`;
    }
    
    // Si es una URL corta de YouTube, extraemos el ID y construimos la URL completa
    if (url.includes('youtu.be/')) {
      // Extraer el ID del video, ignorando parámetros adicionales
      const match = url.match(/youtu\.be\/([a-zA-Z0-9_-]{11})/);
      if (match && match[1]) {
        return `https://www.youtube.com/watch?v=${match[1]}`;
      }
    }
    
    // Si no podemos procesar la URL, devolvemos la original
    return url;
  };

  useEffect(() => {
    if (mediaList.length <= 1) return;
    const media = mediaList[current];
    
    // Limpiar timeout anterior
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (media.type === 'image') {
      // Para imágenes, usar el intervalo configurado
      timeoutRef.current = setTimeout(() => {
        setCurrent((prev) => (prev + 1) % mediaList.length);
      }, interval);
    } else if (media.type === 'video' || media.type === 'youtube') {
      // Para videos con audio habilitado pero sin interacción, usar el intervalo configurado
      // Para videos sin audio o con interacción, usar la duración del video
      let displayTime = interval; // fallback por defecto
      
      if (!enableAudio || userInteracted) {
        // Si no hay audio habilitado o el usuario ya interactuó, usar duración del video
        displayTime = videoDuration > 0 ? videoDuration * 1000 : interval;
      }
      
      timeoutRef.current = setTimeout(() => {
        setCurrent((prev) => (prev + 1) % mediaList.length);
      }, displayTime);
    }

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [current, interval, mediaList, videoDuration, enableAudio, userInteracted]);

  // Agregar event listeners para detectar interacción
  useEffect(() => {
    const events = ['click', 'touchstart', 'keydown', 'scroll', 'mousemove'];
    
    const handleInteraction = () => {
      setUserInteracted(true);
      // Remover listeners después de la primera interacción
      events.forEach(event => {
        document.removeEventListener(event, handleInteraction);
      });
    };

    // Solo agregar listeners si el audio está habilitado y no ha habido interacción
    if (enableAudio && !userInteracted) {
      events.forEach(event => {
        document.addEventListener(event, handleInteraction, { once: true });
      });
    }

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleInteraction);
      });
    };
  }, [enableAudio, userInteracted]);

  if (mediaList.length === 0) return null;
  const media = mediaList[current];

  const handleVideoDuration = (duration: number) => {
    setVideoDuration(duration);
  };

  const handleVideoError = (error: any) => {
    // Si hay error al cargar el video, usar el intervalo por defecto
    setVideoDuration(0);
    console.warn('Error al cargar video:', error);
    
    // Si es un error de YouTube, mostrar información específica
    if (media.type === 'youtube') {
      console.warn('Para videos de YouTube, asegúrate de usar una URL válida como:');
      console.warn('- https://www.youtube.com/watch?v=VIDEO_ID');
      console.warn('- https://youtu.be/VIDEO_ID');
      console.warn('- Solo el ID del video (11 caracteres)');
    }
  };

  const handleVideoReady = () => {
    // Video listo para reproducir
  };

  const handleVideoStart = () => {
    // Video comenzó a reproducir
  };

  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#000' }}>
      {media.type === 'image' ? (
        <img
          src={media.url}
          alt={`media-${current}`}
          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
        />
      ) : media.type === 'youtube' ? (
        <ReactPlayer
          url={processYouTubeUrl(media.url)}
          width="100%"
          height="100%"
          playing={true}
          loop={false}
          controls={enableAudio}
          muted={!enableAudio}
          onDuration={handleVideoDuration}
          onError={handleVideoError}
          style={{ objectFit: 'contain' }}
        />
      ) : useNativeVideo ? (
        <video
          src={media.url}
          autoPlay
          muted
          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          onLoadedMetadata={(e) => handleVideoDuration(e.currentTarget.duration)}
          onError={handleVideoError}
          onCanPlay={handleVideoReady}
          onPlay={handleVideoStart}
          controls={enableAudio}
        />
      ) : (
        <ReactPlayer
          url={media.url}
          width="100%"
          height="100%"
          playing={true}
          loop={true}
          controls={enableAudio || media.type === 'video'} // Siempre mostrar controles para videos normales
          muted={!enableAudio}
          onDuration={handleVideoDuration}
          onError={handleVideoError}
          onReady={handleVideoReady}
          onStart={handleVideoStart}
          style={{ objectFit: 'contain' }}
        />
      )}
    </div>
  );
};

export default CarouselMedia; 